import { Hash, MessageSquarePlus } from 'lucide-react'
import { NavLink } from 'react-router-dom'

import { Avatar, Button, EmptyState, ErrorBanner, Spinner, cn, relativeTime } from '@/features/issue'

import type { ChatChannel } from './types'

export function channelTitle(channel: ChatChannel): string {
  if (channel.kind === 'dm') return channel.peer?.name ?? 'Direct message'
  return channel.name || 'Group'
}

function ChannelIcon({ channel }: { channel: ChatChannel }) {
  if (channel.kind === 'dm') return <Avatar user={channel.peer ?? null} size="md" />
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-ink-100 text-ink-600 dark:bg-ink-800 dark:text-ink-300"
    >
      <Hash className="size-4" />
    </span>
  )
}

export function ChannelList({
  channels,
  isLoading,
  error,
  onRetry,
  activeId,
  basePath,
  onNew,
}: {
  channels: ChatChannel[]
  isLoading: boolean
  error: unknown
  onRetry: () => void
  activeId: string | null
  basePath: string
  onNew: () => void
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-ink-200 px-4 py-3 dark:border-ink-800">
        <h1 className="text-base font-semibold tracking-tight">Chat</h1>
        <Button size="sm" variant="primary" onClick={onNew} aria-label="New message">
          <MessageSquarePlus className="size-4" aria-hidden="true" />
          New
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error ? (
          <ErrorBanner error={error} onRetry={onRetry} className="m-3" />
        ) : isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : channels.length === 0 ? (
          <EmptyState
            title="No conversations yet"
            description="Message a teammate directly or start a group for your project."
            action={
              <Button size="sm" variant="primary" onClick={onNew}>
                Start a conversation
              </Button>
            }
          />
        ) : (
          <ul className="p-2">
            {channels.map((channel) => {
              const active = channel.id === activeId
              const preview = channel.last_message
                ? channel.last_message.deleted_at
                  ? 'Message deleted'
                  : channel.last_message.body
                : channel.kind === 'group'
                  ? `${channel.members.length} members`
                  : 'Say hello'
              return (
                <li key={channel.id}>
                  <NavLink
                    to={`${basePath}/${channel.id}`}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-2.5 py-2 transition',
                      active
                        ? 'bg-brand-50 dark:bg-brand-950'
                        : 'hover:bg-ink-100 dark:hover:bg-ink-800',
                    )}
                  >
                    <ChannelIcon channel={channel} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span
                          className={cn(
                            'truncate text-sm',
                            channel.unread_count > 0
                              ? 'font-semibold text-ink-900 dark:text-ink-50'
                              : 'font-medium text-ink-800 dark:text-ink-100',
                          )}
                        >
                          {channelTitle(channel)}
                        </span>
                        {channel.last_message_at ? (
                          <span className="shrink-0 text-[11px] text-ink-400">
                            {relativeTime(channel.last_message_at)}
                          </span>
                        ) : null}
                      </span>
                      <span className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'truncate text-xs',
                            channel.unread_count > 0
                              ? 'text-ink-700 dark:text-ink-200'
                              : 'text-ink-500 dark:text-ink-400',
                          )}
                        >
                          {channel.last_message?.author && channel.kind === 'group'
                            ? `${channel.last_message.author.name.split(' ')[0]}: `
                            : ''}
                          {preview}
                        </span>
                        {channel.unread_count > 0 ? (
                          <span
                            aria-label={`${channel.unread_count} unread`}
                            className="inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-brand-600 px-1.5 text-[11px] font-semibold text-white"
                          >
                            {channel.unread_count > 99 ? '99+' : channel.unread_count}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </NavLink>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
